/**
 * A4 (vague « Fluidité ») — Position d'une action sur le plan, dans la fiche de l'action.
 *
 * - Action placée : vignette du plan avec l'épingle (rendu serveur), « Voir sur le plan »
 *   (ouvre le plan centré sur l'épingle), « Déplacer » pour la maîtrise d'œuvre.
 * - Action non placée (y compris un marqueur de visite ou une proposition du Fil) :
 *   « Placer sur le plan » → choix du plan en vigueur → toucher l'endroit.
 *
 * Même corps sur le terrain et au bureau (frontend/src/components/project/plans/EpinglePlan.jsx).
 */
import { useEffect, useState } from 'react';
import api from './planApi'; // bureau : adaptateur (même interface que le client du terrain)
import PlanAnnote, { Feuille } from './PlanAnnote';

// ---- corps partagé ----
const bouton = {
  minHeight: 40, padding: '0 12px', border: '1px solid #E0E0DB', background: 'white', color: '#2C2C2C',
  fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', display: 'inline-flex',
  alignItems: 'center', gap: 6,
};

function plansAPlat(data) {
  const out = [];
  (data?.lots || []).forEach((s) => (s.plans || []).forEach((p) => out.push({ ...p, section: [s.lot_code, s.lot_nom].filter(Boolean).join(' · ') })));
  (data?.generaux || []).forEach((p) => out.push({ ...p, section: 'Plans généraux' }));
  return out;
}

export function ChoixPlan({ chantierId, onChoisir, onFermer }) {
  const [plans, setPlans] = useState(null);
  const [erreur, setErreur] = useState(null);
  useEffect(() => {
    api.get(`/chantiers/${chantierId}/plans-en-vigueur`).then((d) => setPlans(plansAPlat(d))).catch((e) => setErreur(e.message));
  }, [chantierId]);
  return (
    <Feuille titre="Sur quel plan ?" onFermer={onFermer} testid="epingle-choix-plan">
      {erreur && <p style={{ color: '#8B3A3A' }}>{erreur}</p>}
      {!plans && !erreur && <p style={{ color: '#6B6B6B' }}>Chargement…</p>}
      {plans && plans.length === 0 && <p style={{ color: '#6B6B6B' }}>Aucun plan déposé sur ce chantier.</p>}
      {(plans || []).map((p) => (
        <button key={p.cle} onClick={() => onChoisir(p)}
          style={{ ...bouton, width: '100%', minHeight: 52, justifyContent: 'space-between', marginBottom: 6, textAlign: 'left' }}
          data-testid={`epingle-plan-${p.cle}`}>
          <span>
            <span style={{ display: 'block', fontSize: 15 }}>{p.titre}</span>
            <span style={{ display: 'block', fontSize: 12, color: '#6B6B6B', fontWeight: 400 }}>{p.section}</span>
          </span>
          <span style={{ fontSize: 12, color: 'white', background: '#2D6A4F', padding: '3px 8px' }}>
            {p.en_vigueur?.indice ? `ind. ${p.en_vigueur.indice}` : 'sans indice'}
          </span>
        </button>
      ))}
    </Feuille>
  );
}

export default function EpinglePlan({ chantierId, element, peutPlacer, onChange, onOuvrirAction }) {
  const eid = element?._id || element?.id;
  const [recharge, setRecharge] = useState(0); // après un placement, sans attendre la liste
  const place = !!element?.plan_pin || recharge > 0;
  const [info, setInfo] = useState(null);
  const [vignette, setVignette] = useState(null);
  const [choix, setChoix] = useState(false);
  const [ouvert, setOuvert] = useState(null); // { cle, placement?, focus? }

  useEffect(() => {
    if (!place || !eid) { setInfo(null); return undefined; }
    let vivant = true;
    let objet = null;
    api.get(`/chantiers/${chantierId}/elements/${eid}/epingle`).then((d) => {
      if (!vivant) return;
      setInfo(d);
      if (d?.epingle) {
        api.getBlob(`/chantiers/${chantierId}/elements/${eid}/epingle/vignette`).then((b) => {
          objet = URL.createObjectURL(b);
          if (vivant) setVignette(objet);
        }).catch(() => {});
      }
    }).catch(() => {});
    return () => { vivant = false; if (objet) URL.revokeObjectURL(objet); };
  }, [chantierId, eid, place, recharge, element?.plan_pin?.x, element?.plan_pin?.y, element?.plan_pin?.plan_key]);

  if (!eid) return null;
  const titre = element.titre || element.texte_brut || 'Action';
  const fin = () => { setOuvert(null); setRecharge((n) => n + 1); if (onChange) onChange(); };

  return (
    <div style={{ padding: '0 14px 8px' }} data-testid="epingle-plan" onClick={(e) => e.stopPropagation()}>
      {place && info?.epingle && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', border: '1px solid #E0E0DB', background: '#FAFAF8', padding: 6 }}>
          <button onClick={() => setOuvert({ cle: info.plan.cle, focus: eid })} aria-label="Voir sur le plan"
            style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', flexShrink: 0 }}>
            {vignette
              ? <img src={vignette} alt={`Position sur ${info.plan.titre}`} style={{ width: 88, height: 88, objectFit: 'cover', display: 'block', border: '1px solid #E0E0DB' }} data-testid="epingle-vignette" />
              : <span style={{ width: 88, height: 88, display: 'block', background: '#EEE' }} />}
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#2C2C2C' }}>{info.plan.titre}</p>
            <p style={{ margin: '2px 0 6px', fontSize: 12, color: '#6B6B6B' }}>
              {info.plan.indice ? `Indice ${info.plan.indice} en vigueur` : 'Sans indice'}
              {info.epingle.indice_pose && info.epingle.indice_pose !== info.plan.indice ? ` · placée sur l'indice ${info.epingle.indice_pose}` : ''}
            </p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button onClick={() => setOuvert({ cle: info.plan.cle, focus: eid })} style={bouton} data-testid="epingle-voir">Voir sur le plan</button>
              {peutPlacer && (
                <button onClick={() => setOuvert({ cle: info.plan.cle, placement: true })} style={bouton} data-testid="epingle-deplacer">Déplacer</button>
              )}
            </div>
          </div>
        </div>
      )}
      {place && info && !info.epingle && info.plan_indisponible && (
        <p style={{ margin: 0, fontSize: 12, color: '#6B6B6B' }}>Placée sur un plan qui ne vous est pas diffusé.</p>
      )}
      {(!place || (info && !info.epingle && !info.plan_indisponible)) && peutPlacer && (
        <button onClick={() => setChoix(true)} style={bouton} data-testid="epingle-placer">📍 Placer sur le plan</button>
      )}
      {choix && (
        <ChoixPlan chantierId={chantierId} onFermer={() => setChoix(false)}
          onChoisir={(p) => { setChoix(false); setOuvert({ cle: p.cle, placement: true }); }} />
      )}
      {ouvert && (
        <PlanAnnote chantierId={chantierId} cle={ouvert.cle} onFermer={() => setOuvert(null)}
          focusPin={ouvert.focus} onOuvrirAction={onOuvrirAction}
          placement={ouvert.placement ? { id: eid, titre } : null} onPlace={fin} />
      )}
    </div>
  );
}
